import { CalendarIcon } from 'lucide-react';

import { Button } from '~/shadcn/components/ui/Button';
import { Calendar } from '~/shadcn/components/ui/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '~/shadcn/components/ui/Popover';
import { cn } from '~/shadcn/lib/utils';
import { getWeekNumber } from '~/utils/dateUtils/dateUtils';
import dayjs from '~/utils/dayjsConfig';

/**
 * Date picker that automatically converts the selected date to a week number and year.
 *
 * @param {Object} props - The component props
 * @param {Date} props.date - The currently selected date
 * @param {Date} props.disabledDates - The disabled condition for the date picker
 * @param {Function} props.onSelectDate - Callback function called when a new date is selected
 * @param {boolean} props.popoverOpen - Controls whether the date picker popover is open
 * @param {Function} props.setPopoverOpen - Callback to set the popover's open state
 * @returns {JSX.Element} A date picker component showing week number and year
 */
function DateWeekInput({ date, disabledDates, onSelectDate, popoverOpen, setPopoverOpen }) {
    let year = date?.getFullYear();

    if (dayjs(date).isBetween(dayjs('2025-12-24'), dayjs('2025-12-31'), 'day', '[]')) {
        year = 2026;
    }

    return (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className={cn('grow justify-start text-left font-normal', !date && 'text-muted-foreground')}
                >
                    <p className="mr-2 space-x-1">
                        <span className="font-medium">Week</span>
                        <span>{getWeekNumber(date, 'YYYY-MM-DD')}</span>
                    </p>
                    <p className="space-x-1">
                        <span className="font-medium">Year</span>
                        <span>{year}</span>
                    </p>
                    <CalendarIcon className="ml-auto mb-[2px] text-muted-foreground" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0 pointer-events-auto">
                <Calendar
                    disabled={disabledDates}
                    mode="single"
                    selected={date}
                    onSelect={(date) => onSelectDate(date)}
                    initialFocus
                />
            </PopoverContent>
        </Popover>
    );
}

export default DateWeekInput;
